import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { Opportunity } from "../lib/utils";
import { formatDate, formatRelativeTime, getStatusColor, getMatchScoreColor, getMatchScoreBg, truncate } from "../lib/utils";
import { Card, Badge, Button, LoadingSpinner, EmptyState, Modal, Textarea, Label } from "../components/ui";

export function Opportunities() {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState("matched");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [selectedOpp, setSelectedOpp] = useState<Opportunity | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    loadOpportunities();
  }, [status, page]);

  const loadOpportunities = async () => {
    try {
      setLoading(true);
      const data = await api.opportunities.list({ status, limit: 20, offset: page * 20 });
      if (page === 0) {
        setOpportunities(data.opportunities);
      } else {
        setOpportunities(prev => [...prev, ...data.opportunities]);
      }
      setHasMore(data.opportunities.length === 20);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load opportunities");
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => setPage(p => p + 1);

  const handleViewDetail = (opp: Opportunity) => {
    setSelectedOpp(opp);
    setShowDetail(true);
  };

  const statusOptions = [
    { value: "new", label: "New" },
    { value: "enriched", label: "Enriched" },
    { value: "matched", label: "Matched" },
    { value: "pitched", label: "Pitched" },
    { value: "applied", label: "Applied" },
    { value: "rejected", label: "Rejected" },
    { value: "archived", label: "Archived" }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Opportunities</h1>
          <p className="text-slate-500 mt-1">Browse and manage discovered opportunities</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={status}
            onChange={e => { setStatus(e.target.value); setPage(0); }}
            className="input w-auto"
          >
            {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        </div>
      </div>

      {error && (
        <div className="card p-4 border-red-200 bg-red-50 text-red-700">
          {error}
        </div>
      )}

      {loading && opportunities.length === 0 ? (
        <LoadingSpinner size="lg" />
      ) : (
        <>
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Company</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Match</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {opportunities.map((opp) => (
                    <tr key={opp.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-slate-900 truncate max-w-xs">{opp.title}</p>
                          <p className="text-sm text-slate-500 truncate max-w-xs">{truncate(opp.description, 80)}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-slate-900">{opp.company_name || "Unknown"}</p>
                        {opp.location && <p className="text-sm text-slate-500">{opp.location}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className="bg-slate-100 text-slate-700">{opp.source}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getMatchScoreBg(opp.match_score)} ${getMatchScoreColor(opp.match_score)}`}>
                          {(opp.match_score * 100).toFixed(0)}%
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={getStatusColor(opp.status)}>{opp.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {formatRelativeTime(opp.fetched_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="ghost" icon="eye" onClick={() => handleViewDetail(opp)}>
                            View
                          </Button>
                          <a href={opp.url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                            Source
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {hasMore && (
              <div className="p-4 border-t border-slate-200">
                <Button onClick={loadMore} className="w-full" variant="secondary">
                  Load More
                </Button>
              </div>
            )}
            
            {opportunities.length === 0 && !loading && (
              <EmptyState 
                icon="Briefcase" 
                title="No opportunities found" 
                description={`No ${status} opportunities yet. Try a different status or trigger a fetch.`} 
              />
            )}
          </div>
        </>
      )}

      {/* Detail Modal */}
      <Modal isOpen={showDetail} onClose={() => setShowDetail(false)} title="Opportunity Details" size="lg">
        {selectedOpp && (
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <p className="text-slate-900">{selectedOpp.title}</p>
            </div>
            <div>
              <Label>Company</Label>
              <p className="text-slate-900">{selectedOpp.company_name || "Unknown"}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Source</Label>
                <Badge className="bg-slate-100 text-slate-700">{selectedOpp.source}</Badge>
              </div>
              <div>
                <Label>Match Score</Label>
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getMatchScoreBg(selectedOpp.match_score)} ${getMatchScoreColor(selectedOpp.match_score)}`}>
                  {(selectedOpp.match_score * 100).toFixed(0)}%
                </div>
              </div>
              <div>
                <Label>Status</Label>
                <Badge className={getStatusColor(selectedOpp.status)}>{selectedOpp.status}</Badge>
              </div>
              <div>
                <Label>Location</Label>
                <p className="text-slate-900">{selectedOpp.location || "Not specified"}</p>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <p className="text-slate-700 whitespace-pre-wrap">{selectedOpp.description}</p>
            </div>
            {selectedOpp.tech_stack && selectedOpp.tech_stack.length > 0 && (
              <div>
                <Label>Tech Stack</Label>
                <div className="flex flex-wrap gap-2">
                  {selectedOpp.tech_stack.map((tech: string) => (
                    <Badge key={tech} className="bg-primary-50 text-primary-700">{tech}</Badge>
                  ))}
                </div>
              </div>
            )}
            {selectedOpp.pain_points && selectedOpp.pain_points.length > 0 && (
              <div>
                <Label>Pain Points</Label>
                <div className="flex flex-wrap gap-2">
                  {selectedOpp.pain_points.map((point: string) => (
                    <Badge key={point} className="bg-amber-50 text-amber-700">{point}</Badge>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
              <Button variant="secondary" onClick={() => setShowDetail(false)}>Close</Button>
              <a href={selectedOpp.url} target="_blank" rel="noopener noreferrer">
                <Button icon="eye">View Source</Button>
              </a>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}